import { useEffect, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { Ban, BadgeCheck, Bot, ChevronLeft, ShieldCheck, Users } from 'lucide-react'
import { useT } from '@/i18n'
import { PageHeader } from '@/shell/AppShell'
import { fetchGroup, banGroup, unbanGroup, verifyGroup, type AdminGroupDetail } from '@/lib/adapters'
import { PERMISSIONS } from '@/lib/permissions'
import { InfoRow, MonoValue, RelativeTime } from '@/components/DataDisplay'
import { StatusBadge, Tag } from '@/components/StatusBadge'
import { ErrorState, PermissionDenied, Skeleton } from '@/components/States'
import { DangerDialog } from '@/components/DangerDialog'
import { useAuth } from '@/providers/AuthProvider'
import { useToast } from '@/providers/UiProviders'

/**
 * Картка групи/каналу - GET /admin/groups/:id. Бан/верифікація - реальні дії через
 * EventFlow command bus (ChannelAggregate.BanChannel/SetChannelVerified). Кік/бан
 * окремого учасника - не реалізовано (вимагає прав чат-адміна на рівні агрегату,
 * не адмін-панелі). Підекрани: тільки ті, що мають реальні дані (members, admins,
 * bots) - messages/media/settings/reports/invites/join-requests/stats/growth/boosts/
 * topics/admin-log не мали підкріплення і були прибрані.
 */
export function GroupCard() {
  const t = useT()
  const { can } = useAuth()
  const { toast } = useToast()
  const { id = '' } = useParams()
  const [group, setGroup] = useState<AdminGroupDetail | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<Error | null>(null)
  const [reload, setReload] = useState(0)
  const [banOpen, setBanOpen] = useState(false)

  useEffect(() => {
    const ac = new AbortController()
    let alive = true
    setLoading(true)
    setError(null)
    fetchGroup(Number(id), ac.signal)
      .then((g) => {
        if (alive) setGroup(g)
      })
      .catch((e: unknown) => {
        if (!alive || (e instanceof DOMException && e.name === 'AbortError')) return
        setError(e instanceof Error ? e : new Error(String(e)))
      })
      .finally(() => {
        if (alive) setLoading(false)
      })
    return () => {
      alive = false
      ac.abort()
    }
  }, [id, reload])

  if (!can(PERMISSIONS.USERS_VIEW)) return <PermissionDenied permission={PERMISSIONS.USERS_VIEW} />

  const canModerate = can(PERMISSIONS.USERS_MODERATE)

  const runSimple = async (fn: () => Promise<unknown>) => {
    try {
      await fn()
      toast(t.users.actionDone, { tone: 'success' })
      setReload((n) => n + 1)
    } catch (e) {
      toast(t.users.actionFailed, {
        tone: 'error',
        ...(e instanceof Error && e.message ? { description: e.message } : {}),
      })
    }
  }

  return (
    <>
      <PageHeader
        title={group ? group.title || (group.username ? `@${group.username}` : `#${group.id}`) : t.groups.cardTitle}
        screenId="A4.04"
        actions={
          <div className="flex flex-wrap items-center gap-1.5">
            {canModerate && group && (
              <>
                {group.is_banned ? (
                  <button
                    type="button"
                    onClick={() => void runSimple(() => unbanGroup(group.id))}
                    className="inline-flex items-center gap-1.5 rounded-md border border-border-default bg-surface-2 px-2.5 py-1 text-sm text-secondary hover:border-border-strong hover:text-primary"
                  >
                    <ShieldCheck size={13} />
                    {t.users.unban}
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={() => setBanOpen(true)}
                    className="inline-flex items-center gap-1.5 rounded-md border border-danger bg-danger-subtle px-2.5 py-1 text-sm text-danger hover:border-danger"
                  >
                    <Ban size={13} />
                    {t.users.ban}
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => void runSimple(() => verifyGroup(group.id, !group.verified))}
                  className="inline-flex items-center gap-1.5 rounded-md border border-border-default bg-surface-2 px-2.5 py-1 text-sm text-secondary hover:border-border-strong hover:text-primary"
                >
                  <BadgeCheck size={13} />
                  {group.verified ? t.users.unverify : t.users.verify}
                </button>
              </>
            )}
            <Link
              to={`/groups/${id}/members`}
              className="inline-flex items-center gap-1.5 rounded-md border border-border-default bg-surface-2 px-2.5 py-1 text-sm text-secondary hover:border-border-strong hover:text-primary"
            >
              <Users size={13} />
              {t.groups.members}
            </Link>
            <Link
              to={`/groups/${id}/admins`}
              className="inline-flex items-center gap-1.5 rounded-md border border-border-default bg-surface-2 px-2.5 py-1 text-sm text-secondary hover:border-border-strong hover:text-primary"
            >
              <ShieldCheck size={13} />
              {t.groups.admins}
            </Link>
            <Link
              to={`/groups/${id}/bots`}
              className="inline-flex items-center gap-1.5 rounded-md border border-border-default bg-surface-2 px-2.5 py-1 text-sm text-secondary hover:border-border-strong hover:text-primary"
            >
              <Bot size={13} />
              {t.groups.botsTitle}
            </Link>
            <Link
              to="/groups"
              className="inline-flex items-center gap-1 rounded-md border border-border-default bg-surface-2 px-2.5 py-1 text-sm text-secondary hover:text-primary"
            >
              <ChevronLeft size={13} />
              {t.nav.groups}
            </Link>
          </div>
        }
      />
      <div className="min-h-0 flex-1 overflow-auto p-3">
        {loading ? (
          <div className="max-w-2xl space-y-2 rounded-lg border border-border-default bg-surface-2 p-4">
            {Array.from({ length: 6 }, (_, i) => (
              <Skeleton key={i} className="h-4 w-2/3" />
            ))}
          </div>
        ) : error ? (
          <ErrorState code={error.message} onRetry={() => setReload((n) => n + 1)} />
        ) : !group ? (
          <ErrorState title={t.states.notFoundTitle} />
        ) : (
          <div className="max-w-2xl rounded-lg border border-border-default bg-surface-2 p-4">
            <div className="mb-3 flex items-center gap-2">
              <StatusBadge
                status={group.is_banned ? 'banned' : group.type === 'channel' ? 'review' : 'active'}
                label={group.is_banned ? t.status.banned : group.type === 'channel' ? t.groups.channel : t.groups.group}
              />
              {group.is_forum && <Tag>{t.groups.forum}</Tag>}
              {group.verified && <Tag>{t.users.verify}</Tag>}
            </div>
            <InfoRow label={t.col.id}>
              <MonoValue value={String(group.id)} />
            </InfoRow>
            <InfoRow label={t.col.title}>{group.title || '—'}</InfoRow>
            <InfoRow label={t.col.username}>{group.username ? `@${group.username}` : '—'}</InfoRow>
            <InfoRow label={t.groups.about}>{group.about || '—'}</InfoRow>
            <InfoRow label={t.groups.members}>
              <span className="font-mono tabular">{group.member_count.toLocaleString()}</span>
            </InfoRow>
            <InfoRow label={t.groups.owner}>
              {group.owner_id ? (
                <Link to={`/users/${group.owner_id}`} className="text-accent hover:underline">
                  {group.owner_username ? `@${group.owner_username}` : `#${group.owner_id}`}
                </Link>
              ) : (
                '—'
              )}
            </InfoRow>
            {group.is_banned && (
              <InfoRow label={t.users.bannedReason}>
                <span className="text-danger">{group.ban_reason || '—'}</span>
              </InfoRow>
            )}
            <InfoRow label={t.col.registered}>
              <RelativeTime value={group.created_at} />
            </InfoRow>
          </div>
        )}
      </div>

      {banOpen && group && (
        <DangerDialog
          open
          onClose={() => setBanOpen(false)}
          screenId="A4.27"
          level={2}
          title={t.users.banTitle(group.title || `#${group.id}`)}
          description={t.users.banDesc}
          confirmLabel={t.users.ban}
          onConfirm={async ({ reason }) => {
            try {
              await banGroup(group.id, reason)
              toast(t.users.actionDone, { tone: 'success' })
              setReload((n) => n + 1)
            } catch (e) {
              toast(t.users.actionFailed, {
                tone: 'error',
                ...(e instanceof Error && e.message ? { description: e.message } : {}),
              })
              throw e
            }
          }}
        />
      )}
    </>
  )
}
